/* ------------------------------------------------------------------ *
 * TattUp AI Generator → teeinblue design handoff (PDP side).
 *
 * The generator redirects to this product page with the design attached
 * as query params (see pdp-teeinblue-handoff.md):
 *
 *   /products/xs-tattoo?design_url=<ENCODED>&design_id=<ENCODED>
 *
 * design_url is a same-origin App Proxy path
 * (/apps/tattup/design-image?src=...) that serves the generated image
 * CORS-safe. This script fetches it, wraps it in a File and injects it
 * into teeinblue's photo-upload <input type="file"> so the customer
 * skips the manual download + re-upload.
 *
 * IMPORTANT: URLSearchParams.get() already decoded design_url one level.
 * The inner src= stays percent-encoded on purpose (the proxy decodes it)
 * — never decodeURIComponent() the value, never touch the inner src.
 * ------------------------------------------------------------------ */

(function () {
  "use strict";

  function log() {
    var args = ["[tattup-handoff]"].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }

  log("script loaded", {
    href: window.location.href,
    search: window.location.search,
    readyState: document.readyState,
  });

  var params = new URLSearchParams(window.location.search);
  var designUrl = params.get("design_url"); // decoded ONE level — use as-is
  var designId = params.get("design_id") || "";

  log("params parsed", { designUrl: designUrl, designId: designId });

  // No handoff params: normal PDP visit, do nothing.
  if (!designUrl) {
    log("EXIT: no design_url param — normal PDP visit, doing nothing");
    return;
  }

  // Only our own App Proxy path is ever sent. Anything else in the param
  // was not produced by the generator — ignore it (also keeps the fetch
  // same-origin, so no CORS and no open-redirect style abuse).
  if (designUrl.indexOf("/apps/tattup/") !== 0) {
    log(
      "EXIT: design_url does not start with /apps/tattup/ — refusing to fetch it.",
      "Got:",
      designUrl,
    );
    return;
  }

  // Guard against double-execution if the asset ever gets included twice.
  if (window.__tattupHandoffStarted) {
    log("EXIT: already started once on this page (double-include guard)");
    return;
  }
  window.__tattupHandoffStarted = true;

  // teeinblue's photo-upload input, most specific first. The personalizer
  // can offer a second upload ("Tattoo 2"); querySelector returns the
  // first input in DOM order, which is Tattoo 1 — exactly what we want.
  var INPUT_SELECTORS = [
    '.teeinblue-item input[type="file"]',
    '.tee-field input[type="file"]',
    'input[type="file"]',
  ];

  var WAIT_FOR_INPUT_MS = 20000; // teeinblue mounts asynchronously
  var FETCH_TIMEOUT_MS = 20000;
  // Small grace period between the input appearing in the DOM and the
  // inject, so teeinblue's own listeners are attached before we dispatch.
  var SETTLE_MS = 400;

  var isGerman =
    (document.documentElement.lang || "").toLowerCase().indexOf("de") === 0;
  var TEXT = isGerman
    ? {
        failed:
          "Dein Tattoo-Design konnte nicht automatisch übernommen werden.",
        retry: "Erneut versuchen",
        dismiss: "Schließen",
        success: "Dein Design wurde übernommen ✓",
      }
    : {
        failed: "Your tattoo design couldn't be attached automatically.",
        retry: "Try again",
        dismiss: "Dismiss",
        success: "Your design was added ✓",
      };

  function waitForInput(timeoutMs) {
    log("waiting for teeinblue file input…", {
      selectors: INPUT_SELECTORS,
      timeoutMs: timeoutMs,
    });
    return new Promise(function (resolve, reject) {
      function find() {
        for (var i = 0; i < INPUT_SELECTORS.length; i += 1) {
          var el = document.querySelector(INPUT_SELECTORS[i]);
          if (el) {
            log("file input FOUND via selector:", INPUT_SELECTORS[i], el);
            return el;
          }
        }
        return null;
      }

      var found = find();
      if (found) return resolve(found);
      log("no file input in DOM yet — observing mutations");

      var obs = new MutationObserver(function () {
        var el = find();
        if (el) {
          obs.disconnect();
          clearTimeout(timer);
          clearInterval(progress);
          resolve(el);
        }
      });
      obs.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
      // Heartbeat so the console shows we're still waiting, plus a live
      // count of ALL file inputs on the page (helps pick the right selector).
      var waited = 0;
      var progress = setInterval(function () {
        waited += 2000;
        var all = document.querySelectorAll('input[type="file"]');
        log(
          "still waiting for input… " + waited + "ms elapsed;",
          all.length + ' input[type="file"] on page:',
          all,
        );
      }, 2000);
      var timer = setTimeout(function () {
        obs.disconnect();
        clearInterval(progress);
        log("TIMEOUT: no file input appeared within " + timeoutMs + "ms");
        reject(new Error("tattup handoff: upload input not found"));
      }, timeoutMs);
    });
  }

  function fetchDesign(url) {
    var controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, FETCH_TIMEOUT_MS)
      : null;

    log("fetching design image:", url);
    return fetch(url, controller ? { signal: controller.signal } : undefined)
      .then(function (res) {
        log("fetch response", {
          status: res.status,
          ok: res.ok,
          contentType: res.headers.get("content-type"),
        });
        if (!res.ok) {
          throw new Error("tattup handoff: fetch " + res.status);
        }
        return res.blob();
      })
      .then(function (blob) {
        log("blob received", { type: blob.type, sizeBytes: blob.size });
        return blob;
      })
      .finally(function () {
        if (timer) clearTimeout(timer);
      });
  }

  function toFile(blob) {
    var type =
      blob.type && blob.type.indexOf("image/") === 0 ? blob.type : "image/png";
    var ext = (type.split("/")[1] || "png").replace("jpeg", "jpg");
    // designId is opaque (may be a gid:// string) — sanitize for a filename.
    var safeId = String(designId)
      .replace(/[^a-zA-Z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);
    var name = "tattoo-" + (safeId || Date.now()) + "." + ext;
    return new File([blob], name, { type: type });
  }

  function injectInto(input, file) {
    log("injecting file into input", {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      input: input,
      inputId: input.id,
      inputName: input.name,
      inputClasses: input.className,
      inIframe: input.ownerDocument !== document,
    });
    var dt = new DataTransfer();
    dt.items.add(file);
    input.files = dt.files;
    log("input.files set — files.length is now:", input.files.length);
    // Some widgets react to input, others to change — dispatch both.
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
    log("dispatched 'input' and 'change' events on the input");
  }

  // Best-effort traceability: mirror the design id into the theme's
  // product form as a hidden line-item property.
  function stampDesignId() {
    if (!designId) return;
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    log("stamping design id into " + forms.length + " cart/add form(s)");
    for (var i = 0; i < forms.length; i += 1) {
      if (forms[i].querySelector('[name="properties[_tattup_design_id]"]')) {
        continue;
      }
      var input = document.createElement("input");
      input.type = "hidden";
      input.name = "properties[_tattup_design_id]";
      input.value = designId;
      forms[i].appendChild(input);
    }
  }

  // Drop only our params so a refresh doesn't re-trigger the inject;
  // keep everything else (locale, tracking, …) intact.
  function cleanUrl() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete("design_url");
      url.searchParams.delete("design_id");
      var query = url.searchParams.toString();
      history.replaceState(
        {},
        "",
        url.pathname + (query ? "?" + query : "") + url.hash,
      );
    } catch (e) {
      /* cosmetic only — ignore */
    }
  }

  /* --------------------------- feedback UI --------------------------- */

  var BANNER_ID = "tattup-handoff-banner";

  function removeBanner() {
    var el = document.getElementById(BANNER_ID);
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  function baseBannerStyle(el) {
    el.style.cssText =
      "position:fixed;left:50%;bottom:16px;transform:translateX(-50%);" +
      "z-index:1000001;max-width:calc(100vw - 32px);box-sizing:border-box;" +
      "display:flex;align-items:center;gap:12px;padding:12px 16px;" +
      "background:#1a1a1a;color:#fff;border-radius:8px;" +
      "font-size:14px;line-height:1.4;box-shadow:0 4px 16px rgba(0,0,0,.35);";
  }

  function showSuccessToast() {
    removeBanner();
    var toast = document.createElement("div");
    toast.id = BANNER_ID;
    toast.setAttribute("role", "status");
    baseBannerStyle(toast);
    toast.textContent = TEXT.success;
    document.body.appendChild(toast);
    setTimeout(removeBanner, 4000);
  }

  function showRetryBanner() {
    removeBanner();
    var banner = document.createElement("div");
    banner.id = BANNER_ID;
    banner.setAttribute("role", "alert");
    baseBannerStyle(banner);

    var message = document.createElement("span");
    message.textContent = TEXT.failed;

    var retryBtn = document.createElement("button");
    retryBtn.type = "button";
    retryBtn.textContent = TEXT.retry;
    retryBtn.style.cssText =
      "flex:none;border:0;border-radius:6px;padding:8px 14px;cursor:pointer;" +
      "background:#fff;color:#1a1a1a;font-size:14px;font-weight:600;";
    retryBtn.addEventListener("click", function () {
      removeBanner();
      run();
    });

    var closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.setAttribute("aria-label", TEXT.dismiss);
    closeBtn.textContent = "×";
    closeBtn.style.cssText =
      "flex:none;border:0;background:none;color:#fff;cursor:pointer;" +
      "font-size:20px;line-height:1;padding:0 2px;";
    closeBtn.addEventListener("click", removeBanner);

    banner.appendChild(message);
    banner.appendChild(retryBtn);
    banner.appendChild(closeBtn);
    document.body.appendChild(banner);
  }

  /* ------------------------------ flow ------------------------------ */

  var running = false;

  function run() {
    if (running) {
      log("run() called but already running — ignored");
      return;
    }
    running = true;
    log("run() started — fetching image and waiting for input in parallel");

    // Fetch the image and wait for the input in parallel.
    Promise.all([fetchDesign(designUrl), waitForInput(WAIT_FOR_INPUT_MS)])
      .then(function (results) {
        var file = toFile(results[0]);
        var input = results[1];
        log(
          "both ready — file built:",
          file.name,
          "— injecting after " + SETTLE_MS + "ms settle delay",
        );
        return new Promise(function (resolve) {
          setTimeout(function () {
            injectInto(input, file);
            resolve();
          }, SETTLE_MS);
        });
      })
      .then(function () {
        running = false;
        log("SUCCESS — stamping design id, cleaning URL, showing toast");
        stampDesignId();
        cleanUrl();
        showSuccessToast();
      })
      .catch(function (err) {
        running = false;
        log("FAILED:", err && err.message ? err.message : err);
        if (window.console && console.warn) console.warn(err);
        showRetryBanner();
      });
  }

  run();
})();
